import classNames from 'classnames';
import React, { FC, memo, useCallback } from 'react';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { Button } from 'shared/ui/Button';
import { Box, useGetBoxRentalPriceString, useGetBoxRentalPriceStringWithVat } from 'entities/Box';
import { RentOptionDocument } from 'entities/RentOption';
import { useGetBoxesSuggestionQuery } from 'features/SizeCodeCollapse/api/getBoxesSuggestion';
import { UserAccountType } from 'entities/User';
import { roundNumber } from 'shared/utils/helpers/roundNumber';
import { getLocalizedString } from 'shared/utils/helpers/JSONLocalization';
import { useScrollToElement } from 'shared/utils/hooks/useScrollToElement';
import { FeaturesList } from 'shared/ui/FeaturesList';
import { WarehouseForBooking } from 'entities/Warehouse';
import { InvoiceFrequencyType } from 'entities/Invoice';
import { useAppSelector } from 'app/config/storeConfig/hooks';
import { getGlobalSettings } from 'app/appState';
import { SizeCodeType } from 'entities/SizeCode';
import { formatDecimal } from 'shared/utils/helpers/formatDecimal';

interface ModernBoxCardMobileProps {
  box: Box;
  selectedBoxId: string | undefined;
  hasRentDiscounts: boolean;
  hasGeneralDiscounts: boolean;
  onSelectBox: (box: Box) => void;
  onBookBox: (box: Box) => void;
  accountType?: Nullable<UserAccountType>;
  warehouseInfo?: Nullable<WarehouseForBooking>;
}

export const ModernBoxCardMobile: FC<ModernBoxCardMobileProps> = memo((props) => {
  const { box, selectedBoxId, onSelectBox, onBookBox, warehouseInfo, accountType } = props;

  const { t } = useAppTranslation('booking');
  const { scrollToElement, scrollRef } = useScrollToElement('center', 500);

  const globalSettings = useAppSelector(getGlobalSettings);

  const showPriceWithVat = globalSettings?.showPriceWithVat;

  // const rentalPrice = useGetBoxRentalPriceString(box);

  const boxMonthRentalPriceWithVat = useGetBoxRentalPriceStringWithVat(box, InvoiceFrequencyType.MONTH, warehouseInfo, accountType);

  const boxMonthRentalPrice = useGetBoxRentalPriceString(box);

  const rentalPrice = showPriceWithVat ? boxMonthRentalPriceWithVat : boxMonthRentalPrice;

  const isBoxSelected = selectedBoxId === box.boxId;
  const boxImage = box.imageUrl || box.sizeCode?.imageUrl;

  const selectBox = useCallback((): void => {
    onSelectBox(box);
  }, [box, onSelectBox]);

  const bookBox = useCallback((): void => {
    onBookBox(box);
  }, [box, onBookBox]);

  React.useLayoutEffect(() => {
    if (isBoxSelected) {
      scrollToElement();
    }
  }, [isBoxSelected, scrollToElement]);

  return (
    <div
      ref={scrollRef}
      className={classNames('bg-white rounded-lg border border-gray-200 cursor-pointer overflow-hidden', {
        '!border-2 !border-accent !bg-green-50': isBoxSelected,
        'hover:border-gray-300': !isBoxSelected,
      })}
      onClick={selectBox}
    >
      {/* Main Content */}
      <div className="px-4 py-3">
        <div className="flex gap-4 items-start">
          {boxImage && <img src={boxImage} alt={box.name} className="w-16 h-16 object-cover rounded-md flex-shrink-0" />}
          <div className="flex-1">
            <div className="flex gap-2">
              <span className="font-semibold text-lg text-primary font-tektur">{t('Box')}</span>
              <span className="font-semibold text-lg text-primary uppercase font-tektur">{box.name}</span>
            </div>

            <div className="text-sm text-grey flex flex-col">
              {box.sizeCode.sizeCodeType !== SizeCodeType.CUBIC_BOX && (
                <span className="text-sm text-grey">{`${t('Square')} ${formatDecimal(roundNumber(box.width * box.length))} м²`}</span>
              )}
              {formatDecimal(box.width)} м × {formatDecimal(box.length)} м × {formatDecimal(roundNumber(box.height))} м
              <span className="text-sm text-grey">{`${t('Value')} ${formatDecimal(
                roundNumber(box.width * box.length * box.height),
              )} м³`}</span>
            </div>

            {box.description && (
              <div className="text-sm text-grey" dangerouslySetInnerHTML={{ __html: getLocalizedString(box.description) }} />
            )}

            <div className="flex items-end justify-between mt-1 py-1">
              <div className="flex items-center gap-2">
                {box.floor && (
                  <FeaturesList size="sm" features={t('{{floor}}', { floor: getLocalizedString(box.floor.name) })} theme="base" outlined />
                )}
              </div>
              <div className="text-xl font-bold text-primary font-tektur">{rentalPrice}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Book Button - Full width at bottom */}
      <div className="px-4 pb-4">
        <Button onClick={bookBox} theme="primary" className="w-full">
          {t('Book')}
        </Button>
      </div>
    </div>
  );
});

interface ModernBoxesContainerMobileProps {
  sizeCodeId: string;
  selectedBoxId: string | undefined;
  rentDiscounts: RentOptionDocument[];
  generalDiscounts: RentOptionDocument[];
  onSelectBox: (box: Box) => void;
  onBookBox: (box: Box) => void;
  accountType?: Nullable<UserAccountType>;
  warehouseInfo: Nullable<WarehouseForBooking>;
}

export const ModernBoxesContainerMobile: FC<ModernBoxesContainerMobileProps> = memo((props) => {
  const { sizeCodeId, selectedBoxId, rentDiscounts, generalDiscounts, onSelectBox, onBookBox, accountType, warehouseInfo } = props;
  const { data } = useGetBoxesSuggestionQuery(sizeCodeId, { skip: !sizeCodeId });

  const selectBox = useCallback(
    (box: Box): void => {
      onSelectBox(box);
    },
    [onSelectBox],
  );

  const bookBox = useCallback(
    (box: Box): void => {
      onBookBox(box);
    },
    [onBookBox],
  );

  const hasRentDiscounts = rentDiscounts.length > 0;
  const hasGeneralDiscounts = generalDiscounts.filter(({ showAsPromotion }) => showAsPromotion).length > 0;

  return (
    <div className="h-full min-h-0 flex flex-col">
      <div className="flex-1 overflow-y-auto overflow-x-hidden">
        <div className={classNames('space-y-4 px-4 pb-4', { 'blur-[3px] pointer-events-none': data?.noAvailableBoxes })}>
          {Boolean(data?.content.length) &&
            data?.content.map((box) => (
              <ModernBoxCardMobile
                key={box.boxId}
                box={box}
                selectedBoxId={selectedBoxId}
                hasRentDiscounts={hasRentDiscounts}
                hasGeneralDiscounts={hasGeneralDiscounts}
                onSelectBox={selectBox}
                onBookBox={bookBox}
                accountType={accountType}
                warehouseInfo={warehouseInfo}
              />
            ))}
        </div>
      </div>
    </div>
  );
});
